/**
 * AdSense / AdMob-ready ad slot.
 *
 * Environment variables (set in /app/frontend/.env when ready to monetise):
 *   REACT_APP_ADSENSE_CLIENT = "ca-pub-XXXXXXXXXXXXXXXX"
 *   REACT_APP_ADSENSE_SLOT_FOOTER = "1234567890"
 *   REACT_APP_ADSENSE_SLOT_PANEL  = "0987654321"
 *
 * When client is missing, a discreet placeholder is rendered so the layout
 * is stable. When the app is wrapped in Capacitor/Expo for iOS/Android,
 * swap this component's body for the native AdMob call — the slot slots
 * remain the same.
 */
import React, { useEffect, useRef } from "react";
import { warn } from "@/lib/log";

const CLIENT = process.env.REACT_APP_ADSENSE_CLIENT;
const SLOT_FOOTER = process.env.REACT_APP_ADSENSE_SLOT_FOOTER;
const SLOT_PANEL = process.env.REACT_APP_ADSENSE_SLOT_PANEL;

// Load AdSense script once
let adsenseInjected = false;
function ensureAdsense() {
  if (adsenseInjected || !CLIENT) return;
  if (document.querySelector('script[src*="adsbygoogle.js"]')) { adsenseInjected = true; return; }
  const s = document.createElement("script");
  s.async = true;
  s.crossOrigin = "anonymous";
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${CLIENT}`;
  document.head.appendChild(s);
  adsenseInjected = true;
}

export default function AdSlot({ placement = "footer", className = "" }) {
  const insRef = useRef(null);
  const slot = placement === "panel" ? SLOT_PANEL : SLOT_FOOTER;
  const active = Boolean(CLIENT && slot);

  useEffect(() => {
    if (!active) return;
    ensureAdsense();
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (err) {
      warn("AdSlot push failed:", err);
    }
  }, [active]);

  if (!active) {
    if (CLIENT) return null;
    return (
      <div
        className={`w-full rounded-lg border border-dashed border-stoneLine/70 bg-sandDeep/20 text-[10px] uppercase tracking-widest text-muted-foreground/70 text-center py-3 ${className}`}
        data-testid={`ad-placeholder-${placement}`}
      >
        Ad space
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`} data-testid={`ad-slot-${placement}`}>
      <ins
        ref={insRef}
        className="adsbygoogle block"
        style={{ display: "block" }}
        data-ad-client={CLIENT}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
